import { useState, type ComponentProps } from "react"
import { CaretDown, Plus } from "@phosphor-icons/react"
import { Button, Input } from "~/components/design-system/components"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/reusable/Popover"
import { ViewScopeIcon } from "~/components/planning/TableViewSelector"
import { type SavedTableView } from "~/server/api/routers/tableView"
import { resolveViewScope } from "~/server/api/utils/viewAccess"
import { cn } from "~/utils/helpers"

export function MenuRowButton({ className, disabled, ...props }: ComponentProps<"button">) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        "w-full rounded-md flex flex-row items-center gap-2 label-base px-2 py-1.5 text-left",
        disabled
          ? "text-fg-disabled cursor-not-allowed"
          : "hover:bg-component-hover cursor-pointer",
        className,
      )}
      {...props}
    />
  )
}

const SEARCH_THRESHOLD = 8

export function OverflowViewsPopover({
  views,
  userId,
  onSelect,
  onSaveView,
}: {
  views: SavedTableView[]
  userId: string | undefined
  onSelect: (view: SavedTableView) => void
  onSaveView: () => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const needle = query.trim().toLowerCase()
  const shown = needle ? views.filter((v) => v.name.toLowerCase().includes(needle)) : views

  const changeOpen = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) setQuery("")
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <Button variant="neutral" size="sm" aria-label="Show more saved views">
          {`${views.length} more`}
          <CaretDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className="w-[280px] p-0 flex flex-col">
        {views.length > SEARCH_THRESHOLD && (
          <div className="p-2 border-b border-border-base">
            <Input
              autoFocus
              containerClassName="w-full"
              placeholder="Search views"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        )}
        <div className="max-h-[320px] overflow-y-auto p-1 flex flex-col">
          {shown.length === 0 ? (
            <span className="label-base text-fg-subtle px-2 py-1.5">{`No views match "${query}"`}</span>
          ) : (
            shown.map((view) => (
              <MenuRowButton
                key={view.id ?? view.name}
                onClick={() => {
                  changeOpen(false)
                  onSelect(view)
                }}
              >
                <span className="truncate flex-1">{view.name}</span>
                <ViewScopeIcon type={resolveViewScope(view, userId)} />
              </MenuRowButton>
            ))
          )}
        </div>
        <div className="p-1 border-t border-border-base">
          <MenuRowButton
            onClick={() => {
              changeOpen(false)
              onSaveView()
            }}
          >
            <Plus />
            Save current as view
          </MenuRowButton>
        </div>
      </PopoverContent>
    </Popover>
  )
}
